import * as React from 'react'
import { useEffect, useRef, useState, useMemo } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import debounce from 'lodash/debounce'

import { useFastFilters, useWithdrawalsList } from './hooks'
import { Client, Gateway, Withdrawal, WithdrawalRequest } from './types'
import {
  Button,
  InputRef,
  Pagination,
  Table,
  Tag,
  Tooltip,
  Typography,
  Flex,
  message,
} from 'antd'
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons'
import { path } from '../../../../libs'
import { FilterValue } from 'antd/es/table/interface'
import { ColumnsType } from 'antd/lib/table'
import {
  colorTransactionRequestStatus,
  currencyFilterList,
  humanTransactionRequestStatus,
} from '../../const'
import { Manager } from '../../transactions/list/types'
import {
  getColumnSearchDateProps,
  getColumnSearchProps,
} from '../../../../libs/tableFunctions'
import { products } from '../../../../libs/products'
import { withdrawalFilters } from '../const'
import {
  $financeManagers,
  $resetFastFilters,
  financeManagerMounted,
} from './model'
import { useEvent } from 'effector-react'
import { Guard } from '../../../../features/session/guard'
import { formatDigits } from '../../../../libs/utils'
import { useUnit } from 'effector-react/effector-react.mjs'

const { Text } = Typography

export const WithdrawalList = () => {
  const [searchParams] = useSearchParams()
  const isStatusInQuery = searchParams.get('status') !== null

  const {
    withdrawals,
    totalCountOfWithdrawals,
    loading,
    onChangeWithdrawalsList,
  } = useWithdrawalsList()

  const financeManagers = useUnit($financeManagers)
  const resetFastFilters = useEvent($resetFastFilters)
  const { fastFilters } = useFastFilters()

  const [searchText, setSearchText] = useState<{ [key: string]: string }>({})
  const [orderBy, setOrderBy] = useState('')
  const [orderDirection, setOrderDirection] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [filtersInitialized, setFiltersInitialized] = useState(false)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({
    status: [
      'TRANSACTION_REQUEST_STATUS_INTERNAL_ERROR',
      'TRANSACTION_REQUEST_STATUS_PENDING_MANAGER',
      'TRANSACTION_REQUEST_STATUS_VENDOR_ERROR',
      'TRANSACTION_REQUEST_STATUS_FUNDS_RESERVED',
      'TRANSACTION_REQUEST_STATUS_HAS_QUESTIONS',
      'TRANSACTION_REQUEST_STATUS_MISSING_INFORMATION',
    ],
  })

  const searchInput = useRef<InputRef>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const getFastFilterFromQuery = () => {
    const params = new URLSearchParams(location.search)
    return params.get('fast_filter') || 'all'
  }

  const [activeFastFilter, setActiveFastFilter] = useState(
    getFastFilterFromQuery(),
  )

  const debouncedFetch = useRef(
    debounce((params: WithdrawalRequest) => {
      onChangeWithdrawalsList(params)
    }, 200),
  ).current

  const normalizedFilters = useMemo(
    () => ({
      status: filtersState?.status as string[] | undefined,
      product: filtersState?.product as string[] | undefined,
      initialCurrency: filtersState?.initialCurrency as string[] | undefined,
      clientCurrency: filtersState?.clientCurrency as string[] | undefined,
      'changeHistory.user.email': filtersState?.['changeHistory.user.email'] as
        | string[]
        | undefined,
    }),
    [filtersState],
  )

  const fetchList = () => {
    debouncedFetch({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
      filters: normalizedFilters,
    })
  }

  useEffect(() => {
    financeManagerMounted()
  }, [])

  useEffect(() => {
    if (!filtersInitialized) return
    fetchList()
  }, [
    page,
    pageSize,
    orderBy,
    orderDirection,
    searchText,
    normalizedFilters,
    filtersInitialized,
  ])

  useEffect(() => {
    if (fastFilters.length === 0) return
    const filterId = getFastFilterFromQuery()
    const filterExists = fastFilters.some((f) => f.id === filterId)

    if (!filterExists && filterId !== 'all') {
      setActiveFastFilter('all')
      const params = new URLSearchParams(location.search)
      params.delete('fast_filter')
      navigate({ search: params.toString() }, { replace: true })

      const defaultFilters = {
        status: [
          'TRANSACTION_REQUEST_STATUS_INTERNAL_ERROR',
          'TRANSACTION_REQUEST_STATUS_PENDING_MANAGER',
          'TRANSACTION_REQUEST_STATUS_VENDOR_ERROR',
          'TRANSACTION_REQUEST_STATUS_FUNDS_RESERVED',
          'TRANSACTION_REQUEST_STATUS_HAS_QUESTIONS',
          'TRANSACTION_REQUEST_STATUS_MISSING_INFORMATION',
        ],
      }

      setFiltersState(defaultFilters)
      setSearchText({})
      setPage(1)
      setFiltersInitialized(true)
      return
    }

    setActiveFastFilter(filterId)
    setPage(1)
    setSearchText({})

    const filterObj = fastFilters.find((f) => f.id === filterId)

    if (!filterObj || filterId === 'all') {
      const defaultFilters = {
        status: [
          'TRANSACTION_REQUEST_STATUS_INTERNAL_ERROR',
          'TRANSACTION_REQUEST_STATUS_PENDING_MANAGER',
          'TRANSACTION_REQUEST_STATUS_VENDOR_ERROR',
          'TRANSACTION_REQUEST_STATUS_FUNDS_RESERVED',
          'TRANSACTION_REQUEST_STATUS_HAS_QUESTIONS',
          'TRANSACTION_REQUEST_STATUS_MISSING_INFORMATION',
        ],
      }
      setFiltersState(defaultFilters)
      setFiltersInitialized(true)
      return
    }

    try {
      const cleanedWhere =
        '{' + filterObj.where.replace(/(\w+)=(["\d\w]+)/g, '"$1":$2') + '}'
      const where = JSON.parse(cleanedWhere)
      const newFilters: Record<string, FilterValue | null> = {}
      const newSearchText: Record<string, string> = {}

      Object.entries(where).forEach(([key, value]) => {
        if (key === 'status') newFilters.status = value as FilterValue
        else if (key === 'currency')
          newFilters.clientCurrency = [value as string]
        else if (key === 'product') newFilters.product = [value as string]
        else if (key === 'processingProvider')
          newSearchText.processingProvider = value as string
        else if (Array.isArray(value)) {
          newFilters[key] = value.filter((v) =>
            ['string', 'number', 'boolean', 'symbol'].includes(typeof v),
          ) as FilterValue
        } else if (
          ['string', 'number', 'boolean', 'symbol'].includes(typeof value)
        ) {
          newFilters[key] = [value] as FilterValue
        }
      })
      setFiltersState(newFilters)
      setSearchText(newSearchText)
      setFiltersInitialized(true)
    } catch (e) {
      console.error('Failed to parse fastFilter.where:', e)
      setFiltersState({})
      setFiltersInitialized(true)
    }
  }, [location.search, fastFilters])

  useEffect(() => {
    return () => {
      resetFastFilters()
    }
  }, [location.pathname])

  const handleFastFilterClick = (filterId: string) => {
    const params = new URLSearchParams(location.search)
    if (filterId === 'all') {
      params.delete('fast_filter')
    } else {
      params.set('fast_filter', filterId)
    }
    navigate({ search: params.toString() }, { replace: true })
  }

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) setPage(currentPage)
    if (currentPageSize) setPageSize(currentPageSize)
    if (currentOrderBy) setOrderBy(currentOrderBy)
    if (currentOrderDirection)
      setOrderDirection(currentOrderDirection === 'descend' ? '-' : '')
    if (filters) setFiltersState(filters)
  }

  const csvCode = useMemo(() => {
    const csvContent = withdrawals
      .map((item) => Object.values(item).join(','))
      .join('\n')
    return (
      'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)
    )
  }, [withdrawals])

  const sortedFastFilters = [
    ...fastFilters.filter((f) => f.id === 'all'),
    ...fastFilters.filter((f) => f.id === 'vendor_error'),
    ...fastFilters.filter((f) => f.id !== 'all' && f.id !== 'vendor_error'),
  ]

  useEffect(() => {
    if (isStatusInQuery) {
      setFiltersState({
        ...filtersState,
        status: [searchParams.get('status') || ''],
      })
    }
  }, [searchParams])

  const cell: React.CSSProperties = { fontSize: 18 }
  const mono: React.CSSProperties = { fontSize: 18, fontWeight: 500 }
  const monoLink: React.CSSProperties = { fontSize: 18, fontWeight: 600 }
  const secondary: React.CSSProperties = { fontSize: 11, color: 'var(--crm-text-secondary)' }

  const columns: ColumnsType<Withdrawal> = [
    {
      title: 'ID',
      dataIndex: 'transactionId',
      width: 90,
      ...getColumnSearchProps<Withdrawal>(
        'transactionId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: true,
      render: (v: string) => (
        <Flex align="center" gap={4}>
          <a target='_blank' href={path.billing.withdrawal.edit(v)} style={monoLink}>{v}</a>
          <CopyOutlined
            style={{ fontSize: 14, color: 'var(--crm-text-secondary)', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(v); message.success(`ID ${v} copied`) }}
          />
        </Flex>
      ),
    },
    {
      title: 'Client',
      key: 'client',
      width: 200,
      children: [
        {
          title: 'Name',
          dataIndex: 'client',
          key: 'fullName',
          width: 200,
          ...getColumnSearchProps<Withdrawal>(
            'client.fullName',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Client) => (
            <Text style={cell}>{item.fullName}</Text>
          ),
        },
        {
          title: 'ID',
          dataIndex: 'client',
          key: 'clientId',
          width: 90,
          ...getColumnSearchProps<Withdrawal>(
            'clientId',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Client) => (
            <div>
              <a href={path.clients.details(item.clientId)} target="_blank" style={monoLink}>
                {item.clientId}
              </a>
              {item?.isTest && (
                <Tag color="purple" style={{ fontSize: 9, marginLeft: 4 }}>Test</Tag>
              )}
              {item?.isPremium && (
                <Tooltip title="Premium">
                  <Tag color="gold" style={{ fontSize: 9, marginLeft: 2 }}>👑</Tag>
                </Tooltip>
              )}
            </div>
          ),
        },
      ],
    },
    {
      title: 'Payment system',
      key: 'ps',
      children: [
        {
          title: 'Provider',
          width: 100,
          ...getColumnSearchProps<Withdrawal>(
            'processingProvider',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Withdrawal) => (
            <Text style={cell}>{item?.processingProvider}</Text>
          ),
        },
        {
          title: 'Method',
          dataIndex: 'gateway',
          width: 200,
          ...getColumnSearchProps<Withdrawal>(
            'gateway.internalName',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Gateway) => (
            <Text style={cell}>{item?.internalName}</Text>
          ),
        },
      ],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      render: (item: string) => (
        <Tag color={colorTransactionRequestStatus(item)} style={{ fontSize: 12 }}>
          {humanTransactionRequestStatus(item)}
        </Tag>
      ),
      defaultFilteredValue: isStatusInQuery
        ? [searchParams.get('status') || '']
        : null,
      filters: withdrawalFilters(),
      filterSearch: true,
      filteredValue: normalizedFilters.status,
      onFilter: (value: any, record) => record.status == value,
    },
    {
      title: 'TID',
      width: 100,
      ...getColumnSearchProps<Withdrawal>(
        'externalTransaction.externalId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: Withdrawal) => {
        const tid =
          item?.externalTransaction?.externalId ||
          item?.externalPaymentTransaction?.externalId
        return tid ? (
          <Text style={mono}>{tid}</Text>
        ) : (
          <Text type="secondary" style={cell}>—</Text>
        )
      },
    },
    {
      title: 'Initial',
      key: 'initial',
      children: [
        {
          title: 'Amount',
          key: 'initialAmount',
          width: 110,
          sorter: true,
          render: (_: any, r: Withdrawal) => (
            <Text style={mono}>{formatDigits(Number(r.initialAmount))}</Text>
          ),
        },
        {
          title: 'Currency',
          key: 'initialCurrency',
          width: 60,
          filters: currencyFilterList,
          filterSearch: true,
          filteredValue: normalizedFilters.initialCurrency,
          onFilter: (value: any, record) => record.initialCurrency == value,
          render: (_: any, r: Withdrawal) => (
            <Text style={mono}>{r.initialCurrency}</Text>
          ),
        },
      ],
    },
    {
      title: 'Client',
      key: 'clientAmountGroup',
      children: [
        {
          title: 'Amount',
          key: 'clientAmount',
          width: 110,
          sorter: true,
          render: (_: any, r: Withdrawal) => (
            <Text style={mono}>{formatDigits(Number(r.clientAmount))}</Text>
          ),
        },
        {
          title: 'Currency',
          key: 'clientCurrency',
          width: 60,
          filters: currencyFilterList,
          filterSearch: true,
          filteredValue: normalizedFilters.clientCurrency,
          onFilter: (value: any, record) => record.clientCurrency == value,
          render: (_: any, r: Withdrawal) => (
            <Text style={mono}>{r.clientCurrency}</Text>
          ),
        },
      ],
    },
    {
      title: 'Dates',
      key: 'dates',
      width: 180,
      ...getColumnSearchDateProps<Withdrawal>(
        'createdAt',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (_: any, r: Withdrawal) => (
        <div style={{ ...mono, fontSize: 18, lineHeight: 1.6, whiteSpace: 'nowrap' }}>
          <div><span style={secondary}>C </span>{r.humanCreatedAt}</div>
          <div><span style={secondary}>U </span>{r.humanUpdatedAt}</div>
        </div>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'product',
      width: 90,
      filters: products,
      render: (v: string) => (
        <Tag
          color={v === 'headway' ? '#dbb454' : '#50f9ac'}
          style={{ fontSize: 10, color: '#000', textTransform: 'capitalize' }}
        >
          {v}
        </Tag>
      ),
    },
    {
      title: 'Manager',
      dataIndex: 'changeHistory',
      key: 'changeHistory.user.email',
      width: 140,
      filterSearch: true,
      filteredValue: normalizedFilters['changeHistory.user.email'],
      filters: financeManagers.map((item) => ({
        text: item.email,
        value: item.email,
      })),
      render: (item: Manager) => (
        <Text style={cell}>{item?.user?.email}</Text>
      ),
    },
  ]

  return (
    <div>
      {/* Fast filters + CSV */}
      <div
        style={{
          background: 'var(--crm-bg)',
          border: '1px solid var(--crm-border)',
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 10,
        }}
      >
        <Flex gap={6} wrap="wrap" align="center">
          {sortedFastFilters.map((filter) => (
            <Button
              key={filter.id}
              size="large"
              type={activeFastFilter === filter.id ? 'primary' : 'default'}
              onClick={() => handleFastFilterClick(filter.id)}
              style={{ fontSize: 16 }}
            >
              {filter.name}{' '}
              <Tag style={{ fontSize: 16, marginLeft: 4 }}>{filter.count}</Tag>
            </Button>
          ))}
        </Flex>
      </div>

      <Flex justify="flex-end" style={{ marginBottom: 10 }} gap="small">
        <Button size="small" onClick={fetchList}>
          Update Data
        </Button>
        <Guard permission="export_transactions">
          <Button
            size="small"
            icon={<DownloadOutlined />}
            href={csvCode}
            download="withdrawals.csv"
            target="_blank"
          >
            CSV
          </Button>
        </Guard>
      </Flex>

      {/* Table */}
      <Pagination
        style={{ marginBottom: 10 }}
        current={page}
        pageSize={pageSize}
        total={totalCountOfWithdrawals}
        showSizeChanger
        pageSizeOptions={[15, 30, 50, 100]}
        showTotal={(total, range) => `${range[0]}–${range[1]} of ${total}`}
        onChange={(p, ps) => {
          setPage(p)
          setPageSize(ps)
        }}
      />

      <div
        style={{
          background: 'var(--crm-bg)',
          border: '1px solid var(--crm-border)',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <Table
          dataSource={withdrawals}
          columns={columns}
          size="small"
          scroll={{ x: 1400 }}
          loading={loading}
          rowKey="transactionId"
          pagination={{
            current: page,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: [15, 30, 50, 100],
            total: totalCountOfWithdrawals,
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
          }}
          onChange={(pagination, filters, sorter) =>
            handleTableChange({
              // @ts-ignore
              currentPage: pagination.current,
              // @ts-ignore
              currentPageSize: pagination.pageSize,
              // @ts-ignore
              currentOrderDirection: sorter.order,
              // @ts-ignore
              currentOrderBy: sorter.field,
              filters,
            })
          }
        />
      </div>
    </div>
  )
}
